import type { IEventBus, EventHandler, EventSubscription, EventMap } from '../interfaces/IEventBus';
import { errorBoundary } from './ErrorBoundaryService';

interface HandlerWrapper {
	handler: EventHandler<any>;
	once: boolean;
}

export class EventBusService implements IEventBus {
	private static instance: EventBusService;
	private events: Map<string, Set<HandlerWrapper>> = new Map();
	private wildcardHandlers: Set<HandlerWrapper> = new Set();

	private constructor() {
		// Private constructor for singleton
	}

	static getInstance(): EventBusService {
		if (!EventBusService.instance) {
			EventBusService.instance = new EventBusService();
		}
		return EventBusService.instance;
	}

	emit<T>(event: string, data: T): void {
		// Emit to specific event handlers
		const handlers = this.events.get(event);
		if (handlers) {
			const handlersToRemove: HandlerWrapper[] = [];

			handlers.forEach((wrapper) => {
				this.invokeHandler(wrapper, data, event);

				if (wrapper.once) {
					handlersToRemove.push(wrapper);
				}
			});

			// Remove one-time handlers
			handlersToRemove.forEach((wrapper) => handlers.delete(wrapper));
		}

		// Emit to wildcard handlers
		this.wildcardHandlers.forEach((wrapper) => {
			this.invokeHandler(wrapper, { event, data }, '*');
		});
	}

	on<T>(event: string, handler: EventHandler<T>): EventSubscription {
		const wrapper: HandlerWrapper = { handler: handler as EventHandler<any>, once: false };

		if (event === '*') {
			this.wildcardHandlers.add(wrapper);
		} else {
			if (!this.events.has(event)) {
				this.events.set(event, new Set());
			}
			this.events.get(event)!.add(wrapper);
		}

		return {
			unsubscribe: () => this.removeHandler(event, wrapper)
		};
	}

	once<T>(event: string, handler: EventHandler<T>): EventSubscription {
		const wrapper: HandlerWrapper = { handler: handler as EventHandler<any>, once: true };

		if (event === '*') {
			this.wildcardHandlers.add(wrapper);
		} else {
			if (!this.events.has(event)) {
				this.events.set(event, new Set());
			}
			this.events.get(event)!.add(wrapper);
		}

		return {
			unsubscribe: () => this.removeHandler(event, wrapper)
		};
	}

	off(event: string, handler?: EventHandler): void {
		if (event === '*') {
			if (handler) {
				this.wildcardHandlers.forEach((wrapper) => {
					if (wrapper.handler === handler) {
						this.wildcardHandlers.delete(wrapper);
					}
				});
			} else {
				this.wildcardHandlers.clear();
			}
		} else {
			const handlers = this.events.get(event);
			if (handlers) {
				if (handler) {
					handlers.forEach((wrapper) => {
						if (wrapper.handler === handler) {
							handlers.delete(wrapper);
						}
					});
				} else {
					this.events.delete(event);
				}
			}
		}
	}

	clear(event?: string): void {
		if (event) {
			this.events.delete(event);
		} else {
			this.events.clear();
			this.wildcardHandlers.clear();
		}
	}

	getEventNames(): string[] {
		return Array.from(this.events.keys());
	}

	getListenerCount(event: string): number {
		if (event === '*') {
			return this.wildcardHandlers.size;
		}
		return this.events.get(event)?.size || 0;
	}

	private invokeHandler<T>(wrapper: HandlerWrapper, data: T, event: string): void {
		try {
			const result = wrapper.handler(data);
			if (result instanceof Promise) {
				result.catch((error) => {
					errorBoundary.handle(error, {
						service: 'EventBus',
						method: 'invokeHandler',
						additional: { event }
					});
				});
			}
		} catch (error) {
			errorBoundary.handle(error as Error, {
				service: 'EventBus',
				method: 'invokeHandler',
				additional: { event }
			});
		}
	}

	private removeHandler(event: string, wrapper: HandlerWrapper): void {
		if (event === '*') {
			this.wildcardHandlers.delete(wrapper);
		} else {
			const handlers = this.events.get(event);
			if (handlers) {
				handlers.delete(wrapper);
				if (handlers.size === 0) {
					this.events.delete(event);
				}
			}
		}
	}

	destroy(): void {
		this.events.clear();
		this.wildcardHandlers.clear();
	}
}

// Export singleton instance
export const eventBus = EventBusService.getInstance();

// Type-safe event emitter wrapper
export class TypedEventBus {
	private bus: EventBusService;

	constructor(bus: EventBusService = eventBus) {
		this.bus = bus;
	}

	emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void {
		this.bus.emit(event, data);
	}

	on<K extends keyof EventMap>(event: K, handler: EventHandler<EventMap[K]>): EventSubscription {
		return this.bus.on(event, handler);
	}

	once<K extends keyof EventMap>(event: K, handler: EventHandler<EventMap[K]>): EventSubscription {
		return this.bus.once(event, handler);
	}

	off<K extends keyof EventMap>(event: K, handler?: EventHandler<EventMap[K]>): void {
		this.bus.off(event, handler as EventHandler<any>);
	}
}

// Export typed instance
export const typedEventBus = new TypedEventBus();
